/**
 * Vuex actions for the department module.
 *
 * @module store/modules/department/actions
 */
import { graph } from "@/store/api";
import { RootState } from "@/store/types";
import { ActionTree } from "vuex/types";
import { DepartmentState } from "./types";

import { gql } from "@apollo/client/core";
import _ from "lodash";

const actions: ActionTree<DepartmentState, RootState> = {
  /**
   * Adds a new department via GraphQL mutation.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {Object} payload.department - Department data with name, description, and meta
   * @returns {Promise<any>} Created department data
   *
   * @example
   * await this.$store.dispatch('ADD_DEPARTMENT', {
   *   department: { name: 'Sales', description: 'Sales team', meta: {} }
   * });
   */
  ADD_DEPARTMENT: async ({ state, commit }, { department }) => {
    const response = await graph
      .mutate({
        mutation: gql`
          mutation ($department: JSON) {
            accesscontrolAPI {
              addDepartment(department: $department)
            }
          }
        `,
        variables: {
          department,
        },
      })
      .catch(() => {});
    return _.get(response, "data.accesscontrolAPI.addDepartment");
  },

  /**
   * Fetches all departments from the API and commits to store.
   *
   * @param {ActionContext} context - Vuex action context
   * @returns {Promise<Array>} Array of department objects
   *
   * @example
   * const departments = await this.$store.dispatch('FETCH_DEPARTMENTS');
   */
  FETCH_DEPARTMENTS: async ({ commit }) => {
    const response = await graph
      .query({
        query: gql`
          query departments {
            accesscontrolAPI {
              departments
            }
          }
        `,
        fetchPolicy: "no-cache",
      })
      .catch(() => {});

    const departments = _.get(response, "data.accesscontrolAPI.departments", []);
    commit("SET_DEPARTMENTS", departments);
    return departments;
  },
};

export default actions;
